import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { THREAD_TITLE_MAX_LENGTH, featureDisabledMessage } from '@aiai/shared';
import { createThread, fetchProject } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { projectNav, threadsHref } from '../lib/projectNav';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useCanonicalProjectRef } from '../hooks/useCanonicalProjectRef';
import { useCapabilities } from '../hooks/useCapabilities';
import { useFeature } from '../hooks/useEntitlements';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { PageHeader } from '../components/PageHeader';
import { FormField } from '../components/FormField';
import { Callout } from '../components/Callout';
import { Spinner } from '../components/Spinner';
import { ErrorState, LoadingState } from '../components/States';

const BODY_MAX = 10_000;

/** "New thread": an optional title plus the opening post. Creating navigates into the thread. */
export function NewThreadPage() {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { wp } = useWorkspacePath();
  const { can } = useCapabilities();
  const threadsOn = useFeature('chat');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [bodyError, setBodyError] = useState<string | null>(null);

  const projectQuery = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => fetchProject(projectId),
    enabled: !!projectId,
  });
  useCanonicalProjectRef(projectId, projectQuery.data?.code);

  useDocumentTitle(projectQuery.data ? `New thread · ${projectQuery.data.name}` : 'New thread');

  const createMutation = useMutation({
    mutationFn: (input: { title?: string; body: string }) => createThread(projectQuery.data!.id, input),
    onSuccess: ({ thread }) => {
      const project = projectQuery.data!;
      void queryClient.invalidateQueries({ queryKey: qk.threads(project.id) });
      navigate(wp(`${threadsHref(project.kind, project.code ?? project.id)}/${thread.id}`), { replace: true });
    },
  });

  if (projectQuery.isLoading) return <LoadingState label="Loading project…" />;
  if (projectQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(projectQuery.error, 'Failed to load project')}
        onRetry={() => projectQuery.refetch()}
      />
    );
  }

  const project = projectQuery.data!;
  const nav = projectNav(project);
  const base = threadsHref(project.kind, project.code ?? project.id);
  const canStart = can('thread:post', { projectId: project.id });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) {
      setBodyError('Write the opening post first.');
      return;
    }
    setBodyError(null);
    createMutation.mutate({ title: title.trim() || undefined, body: trimmed });
  };

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: nav.rootLabel, to: wp(nav.rootTo) },
          { label: nav.name, to: wp(nav.selfTo) },
          { label: 'Threads', to: wp(base) },
          { label: 'New thread' },
        ]}
      />

      <PageHeader
        title="New thread"
        subtitle="Think out loud with your team and the PM. The thread keeps a running summary and collects the issues it surfaces."
      />

      {threadsOn === false ? (
        <Callout tone="info">{featureDisabledMessage('chat')}</Callout>
      ) : !canStart ? (
        <Callout tone="info">You don&apos;t have permission to start threads in this project.</Callout>
      ) : (
        <form onSubmit={submit} className="card max-w-3xl space-y-4 p-5">
          <FormField label="Title" hint="Optional — the PM names the thread from your first post otherwise.">
            <input
              className="input"
              value={title}
              maxLength={THREAD_TITLE_MAX_LENGTH}
              placeholder="What is this thread about?"
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormField>

          <FormField label="Opening post" error={bodyError}>
            <textarea
              className="input min-h-[12rem]"
              value={body}
              maxLength={BODY_MAX}
              placeholder="Describe the idea, the question or the problem. Markdown works."
              onChange={(e) => setBody(e.target.value)}
            />
          </FormField>

          {createMutation.isError && (
            <Callout tone="danger">{errorMessage(createMutation.error, 'Could not start the thread')}</Callout>
          )}

          <div className="flex items-center justify-end gap-2">
            <Link to={wp(base)} className="btn-secondary">
              Cancel
            </Link>
            <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
              {createMutation.isPending ? <Spinner className="h-3.5 w-3.5" /> : null}
              Start thread
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
